import { useDispatch, useSelector } from "react-redux";
import { getDitaContent, selectDitaContent } from "../Content/ContentSlice";
import { AppHeader, AppLayout, AppSidebar } from "../Layout";
import styles from "./UpdatesPage.module.scss";
import classNames from "classnames";
import UpdateCard from "./UpdateCard";
import { lazy, Suspense, useMemo, useRef, useState } from "react";
import useDebouncedMutationEffect from "../../hooks/useDebouncedMutationEffect";
import { useUpdatesClickAction } from "../../hooks/updatesHooks/useUpdatesClickAction";
import { useSearchParams } from "react-router-dom";
import UpdatesFilters from "./UpdatesFilters";
import useAppModernisation from "../../hooks/useAppModernisation";
import AccountHeader from "../AppAccount/AccountHeader";
import {
  applyFiltersToSearchParams,
  buildGuidelineOptions,
  cardMatchesFilters,
  getTopicCategory,
  parseFiltersFromSearchParams,
  RANGE_KEYS,
  readCardDate,
} from "./updatesFilterUtils";

const DateRangePopup = lazy(() => import("./DateRangePopup"));

const UpdatesPage = () => {
  const ref = useRef(null);
  const { clickAction } = useUpdatesClickAction(ref);
  const content = useSelector(selectDitaContent);
  const dispatch = useDispatch();
  const { isAppMode } = useAppModernisation();
  const updateContent = getDitaContent(
    "Updates/New_and_updated_content_landing_page.ditamap",
    content,
    dispatch,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get("select");

  const filters = parseFiltersFromSearchParams(searchParams);
  const [showCustomRange, setShowCustomRange] = useState(false);

  const cards = useMemo(() => {
    if (!updateContent) return [];
    const topicrefs = Array.from(updateContent.getElementsByTagName("topicref"));
    return topicrefs
      .map((tr) => {
        const href = tr.getAttribute("href") || "";
        if (!href) return null;
        const topicDoc = getDitaContent("Updates/" + href, content, dispatch);
        const category = topicDoc ? getTopicCategory(topicDoc) : null;
        const date = topicDoc ? readCardDate(topicDoc) : null;
        return {
          element: tr,
          href,
          guidelineCode: category,
          guidelineLabel: category,
          date,
        };
      })
      .filter(Boolean);
  }, [updateContent, content, dispatch]);

  const guidelineOptions = useMemo(
    () => buildGuidelineOptions(cards.map((c) => c.guidelineCode)),
    [cards],
  );

  // Earliest valid update date drives the mobile calendar's month count
  const earliestUpdateDate = useMemo(() => {
    const dates = cards.map((c) => c.date).filter(Boolean);
    if (dates.length === 0) return null;
    return new Date(Math.min(...dates.map((d) => d.getTime())));
  }, [cards]);

  const visibleCards = useMemo(
    () => cards.filter((c) => cardMatchesFilters(c, filters)),
    [cards, filters],
  );

  const handleFiltersChange = (next) => {
    setSearchParams(applyFiltersToSearchParams(searchParams, next), {
      replace: true,
    });
  };

  const handleOpenCustomRange = () => setShowCustomRange(true);

  const handleApplyCustomRange = ({ from, to }) => {
    setShowCustomRange(false);
    setSearchParams(
      applyFiltersToSearchParams(searchParams, {
        ...filters,
        range: RANGE_KEYS.CUSTOM,
        from,
        to,
      }),
      { replace: true },
    );
  };

  useDebouncedMutationEffect(ref, clickAction, [], 100);

  const pageContent = (
    <div className={classNames("size-16 regular", styles.updatePage)} ref={ref}>
      <div className={classNames("size-24 medium", styles.pageHeading)}>
        New and updated content
      </div>
      <UpdatesFilters
        filters={filters}
        guidelineOptions={guidelineOptions}
        onChange={handleFiltersChange}
        onOpenCustomRange={handleOpenCustomRange}
      />
      {visibleCards.length === 0 ? (
        <div className={styles.emptyState}>No topics found.</div>
      ) : (
        visibleCards.map((card, index) => (
          <UpdateCard
            key={`update-${card.href}-${index}`}
            update={card.element}
            selected={selected}
            guidelineLabel={card.guidelineLabel}
          />
        ))
      )}
    </div>
  );

  const dateRangePopup = showCustomRange ? (
    <Suspense fallback={null}>
      <DateRangePopup
        initialFrom={filters.from}
        initialTo={filters.to}
        earliestDate={earliestUpdateDate}
        onApply={handleApplyCustomRange}
        onClose={() => setShowCustomRange(false)}
      />
    </Suspense>
  ) : null;

  if (isAppMode) {
    return (
      <>
        <AccountHeader headername="What's new" />
        {pageContent}
        {dateRangePopup}
      </>
    );
  }

  return (
    <AppLayout appSidebar={<AppSidebar />} appHeader={<AppHeader />} useMainContentWrapper={false}>
      {pageContent}
      {dateRangePopup}
    </AppLayout>
  );
};

export default UpdatesPage;
